import { useCallback, useState } from 'react';
import axios from 'axios';
import { MEETING_PREP_CONFIG } from './config';
import { Citation, Report } from './types';

// Self-contained axios instance pointed at the agent_manager service — deliberately separate
// from the app's useAxios so nothing existing is touched. withCredentials sends the session
// cookie for real per-user RBAC; AGENT_BEARER_TOKEN is the local cross-origin fallback.
const client = axios.create({
  baseURL: MEETING_PREP_CONFIG.AGENT_BASE_URL,
  withCredentials: true,
  headers: MEETING_PREP_CONFIG.AGENT_BEARER_TOKEN
    ? { Authorization: `Bearer ${MEETING_PREP_CONFIG.AGENT_BEARER_TOKEN}` }
    : {},
});

// Phase 1: the /brief endpoint reuses the shipped agent runtime, so it returns the same envelope
// as Ask Clara — the briefing is markdown in `reply`, plus citations and an optional Excel report.
export interface BriefingResult {
  reply: string;
  citations: Citation[];
  report: Report | null;
  toolActivity: string[];
  generatedAt: string; // ISO timestamp of when this briefing was generated
}

// Session cache: a briefing is generated once per workflow and reused on later opens (no API call)
// until the page/session is reset. Regenerate forces a fresh call. Cleared on logout via a full
// reload; clearBriefingCache() is exported so it can also be wired into an explicit logout handler.
const briefingCache = new Map<string, BriefingResult>();

export const clearBriefingCache = (): void => briefingCache.clear();

export interface MeetingPrepState {
  briefing: BriefingResult | null;
  loading: boolean;
  error: string | null;
  show: (workflowId: string) => void;
  regenerate: (workflowId: string) => void;
}

export const useMeetingPrep = (): MeetingPrepState => {
  const [briefing, setBriefing] = useState<BriefingResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBrief = useCallback(async (workflowId: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.post(`/agent/${MEETING_PREP_CONFIG.AGENT_ID}/brief`, {
        workflow_id: workflowId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      const payload = res?.data?.messages?.[0];
      if (payload && payload.reply) {
        const result: BriefingResult = {
          reply: payload.reply,
          citations: payload.citations || [],
          report: payload.report || null,
          toolActivity: payload.tool_activity || [],
          generatedAt: new Date().toISOString(),
        };
        briefingCache.set(workflowId, result);
        setBriefing(result);
      } else {
        setError(res?.data?.errors?.[0] || 'No briefing was returned.');
      }
    } catch (e: any) {
      setError(e?.response?.data?.errors?.[0] || e?.message || 'Failed to generate the briefing.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Open a workflow's briefing — serve the cached one if present (no API call), else generate.
  const show = useCallback((workflowId: string) => {
    if (!workflowId) return;
    const cached = briefingCache.get(workflowId);
    if (cached) {
      setBriefing(cached);
      setError(null);
      return;
    }
    fetchBrief(workflowId);
  }, [fetchBrief]);

  // Force a fresh briefing (bypasses and refreshes the cache).
  const regenerate = useCallback((workflowId: string) => {
    if (!workflowId || loading) return;
    fetchBrief(workflowId);
  }, [fetchBrief, loading]);

  return {
    briefing, loading, error, show, regenerate,
  };
};
